import { BaseChartProps } from "@/shared/types/charts";
import { useTheme } from "@mui/material";
import { EChartsOption, GraphicComponentOption, ScatterSeriesOption, SeriesOption } from "echarts";
import ReactECharts from "echarts-for-react";
import { QuadrantLabel } from "./types";
import { quadrantLabelsToGraphics } from "./quadrantLabelsToGraphics";

type BubbleChartProps = BaseChartProps & {
  series: ScatterSeriesOption;
  thresholdLines?: { xAxis: number; yAxis: number };
  graphic?: GraphicComponentOption[] | GraphicComponentOption;
  quadrantLabels?: QuadrantLabel[];
};

/**
 * Example usage of BubbleChart component:
 *
 * const exampleSeries: ScatterSeriesOption = {
 *   name: "Series 1",
 *   type: "scatter",
 *   data: [
 *     { name: "Meta", value: [1, 2, 100] },
 *     { name: "Google", value: [4, 5, 50] },
 *   ],
 *   symbolSize: (data: number[]) => {
 *     return data[2] ?? 0;
 *   },
 * };
 *
 * const exampleThresholdLines: { xAxis: number; yAxis: number } = { xAxis: 2, yAxis: 3 };
 *
 * <BubbleChart
 *   series={exampleSeries}
 *   thresholdLines={exampleThresholdLines}
 *   yAxisLabelFormatter={(value) => `$${value}M`}
 *   xAxisLabelFormatter={(value) => `${value}%`}
 * />
 */

export const BubbleChart = ({
  series,
  thresholdLines,
  yAxisLabelFormatter,
  xAxisLabelFormatter,
  xAxisLabel,
  yAxisLabel,
  graphic,
  quadrantLabels,
  tooltipOptions,
  overrides = {},
}: BubbleChartProps) => {
  const theme = useTheme();
  const seriesData: SeriesOption[] = series ? [series] : [];
  const tooltipPalette = theme.palette.echartTooltip ?? {};
  const textColorPalette = theme.palette.brandedTypography ?? {};

  const graphicComponents: GraphicComponentOption[] | GraphicComponentOption | undefined =
    quadrantLabels ? quadrantLabelsToGraphics(quadrantLabels, theme) : graphic;

  if (thresholdLines) {
    seriesData.push({
      type: "line",
      markLine: {
        silent: true,
        symbol: "none",
        label: { show: false },
        data: [{ xAxis: thresholdLines.xAxis }, { yAxis: thresholdLines.yAxis }],
        lineStyle: {
          type: "dashed",
          color: theme.palette.bubbleChart.dashedLine,
        },
      },
    });
  }

  const baseOption: EChartsOption = {
    grid: {
      left: 20,
      right: 40,
      top: 50,
      bottom: 60,
      containLabel: true,
    },
    series: seriesData,
    xAxis: {
      type: "value", // For numerical data on the x-axis
      z: 999,
      ...(xAxisLabel && {
        name: xAxisLabel,
        nameLocation: "middle",
        nameGap: 40,
        nameTextStyle: {
          color: textColorPalette.textColor || theme.palette.text?.primary,
          fontWeight: 600,
          fontSize: 14,
        },
      }),
      axisLabel: {
        fontSize: 14,
        color: textColorPalette.textColor || theme.palette.text?.primary,
        ...(xAxisLabelFormatter && {
          formatter: xAxisLabelFormatter,
        }),
      },
      splitLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
    },
    yAxis: {
      type: "value", // For numerical data on the y-axis
      z: 999,
      ...(yAxisLabel && {
        name: yAxisLabel,
        nameLocation: "end",
        nameGap: 50,
        nameTextStyle: {
          align: "center",
          verticalAlign: "top",
          color: textColorPalette.textColor || theme.palette.text?.primary,
          fontWeight: 600,
          fontSize: 14,
          padding: [0, 20, 0, 160],
        },
      }),
      axisLabel: {
        fontSize: 14,
        color: textColorPalette.textColor || theme.palette.text?.primary,
        ...(yAxisLabelFormatter && {
          formatter: yAxisLabelFormatter,
        }),
      },
      splitLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
    },
    emphasis: {
      focus: "self",
    },
    tooltip: {
      trigger: "item",
      backgroundColor: tooltipPalette.background || theme.palette.background?.paper || "#fff",
      textStyle: {
        color: tooltipPalette.textColor || theme.palette.text?.primary,
      },
      ...(tooltipOptions ?? {}),
    },
    graphic: graphicComponents,
  };

  const option: EChartsOption = {
    ...baseOption,
    ...overrides,
  };

  return <ReactECharts option={option} style={{ minHeight: "500px" }} notMerge={true} />;
};
